import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import type { JuiImageEntry, JuiImageRejection } from '@jump-tech-frontend/core-components/components';
import {
  CameraI18nKeys,
  isStoredImageUrl,
  PhotoCaptureTrigger,
  StoredImageLoader
} from '@jump-tech-frontend/image-analysis';
import { TRANSLATIONS_PROVIDER } from '../../translations.token';
import { CAMERA_KEYS, REJECTION_KEYS } from '../capture-wording';
import {
  ImageOrFileAnswer,
  ImageOrFileQuestionParams,
  ImageOrFileValue,
  ImageOrFileVm,
  UploadedDocument
} from './image-or-file.model';

export const DEFAULT_DOCUMENT_TYPES = '.doc,.docx,.xls,.xlsx,.pdf';

export const MAX_DOCUMENT_BYTES = 20000000;

interface ImageItem {
  id: string;
  answer: string;
  displaySrc: string;
  displayState: JuiImageEntry['state'];
}

function answersOf(value: ImageOrFileValue | undefined): ImageOrFileAnswer[] {
  if (!value) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}

function isDocument(answer: ImageOrFileAnswer): answer is UploadedDocument {
  return typeof answer === 'object' && answer !== null && 'name' in answer && 'data' in answer;
}

function withoutUploadTimestamp(fileName: string): string {
  return fileName.replace(/_\d{13}(\..*)$/, (_match, extension: string) => extension);
}

export function isAcceptedFile(file: File, accept: string): boolean {
  const extension = `.${file.name.split('.').pop()}`;
  return accept
    .split(',')
    .map(type => type.trim())
    .some(
      type =>
        type === extension || type === file.type || (type.endsWith('/*') && file.type.startsWith(type.slice(0, -2)))
    );
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

@Injectable()
export class ImageOrFilePresenter {
  private readonly storedImages = inject(StoredImageLoader);
  private readonly photoCapture = inject(PhotoCaptureTrigger);
  private readonly translations = inject(TRANSLATIONS_PROVIDER);

  private readonly params = signal<ImageOrFileQuestionParams>({ key: '' });
  private readonly images = signal<ImageItem[]>([]);
  private readonly documents = signal<UploadedDocument[]>([]);
  private readonly rejection = signal<string | null>(null);
  private nextStoredId = 0;
  private emitValue: (value: ImageOrFileAnswer[]) => void = () => undefined;

  public readonly vm = computed<ImageOrFileVm>(() => {
    const { key, accept, sort, examples, readonly } = this.params();
    const hasDocuments = this.documents().length > 0;
    const images = this.images();
    return {
      key,
      readonly: readonly ?? false,
      imageInputId: `image_${key}`,
      fileInputId: `file_${key}`,
      accept: accept || DEFAULT_DOCUMENT_TYPES,
      sort: sort === 'desc' ? 'desc' : 'asc',
      images: images.map(image => ({ id: image.id, src: image.displaySrc, state: image.displayState })),
      documentNames: this.documents().map(document => document.name),
      documentButtonLabel: `${this.translate(hasDocuments ? 'add' : 'upload')} ${this.translate('file')}`,
      documentButtonIcon: hasDocuments ? 'note_add' : 'insert_drive_file',
      examples: images.length || readonly ? [] : (examples ?? []),
      imageQa: this.params().qaHook ?? null,
      rejection: this.rejection()
    };
  });

  constructor() {
    inject(DestroyRef).onDestroy(() => this.images().forEach(image => this.storedImages.release(image.displaySrc)));
  }

  public onValueChange(emit: (value: ImageOrFileAnswer[]) => void): void {
    this.emitValue = emit;
  }

  public load(params: ImageOrFileQuestionParams): void {
    this.params.set(params);
    const answers = answersOf(params.value);
    this.documents.set(answers.filter(isDocument));
    this.syncImages(answers.filter((answer): answer is string => typeof answer === 'string'));
    if (params.value && !Array.isArray(params.value) && !params.readonly) {
      this.emitValue(answers);
    }
  }

  public imagesChanged(entries: JuiImageEntry[]): void {
    this.rejection.set(null);
    const current = this.images();
    const next = entries.map(
      entry =>
        current.find(image => image.id === entry.id) ?? {
          id: entry.id,
          answer: entry.src,
          displaySrc: entry.src,
          displayState: 'ready' as const
        }
    );
    this.release(current.filter(image => !next.includes(image)));
    this.images.set(next);
    this.emitValue(this.formValue());
  }

  public async documentsPicked(files: File[]): Promise<void> {
    this.rejection.set(null);
    const accept = this.vm().accept;
    const picked: UploadedDocument[] = [];
    for (const file of files) {
      const problem = this.documentProblem(file, accept);
      if (problem) {
        this.rejection.set(`${file.name}: ${this.translate(problem)}`);
        continue;
      }
      try {
        picked.push({ name: withoutUploadTimestamp(file.name), data: await readAsDataUrl(file) });
      } catch {
        continue;
      }
    }
    if (picked.length) {
      this.documents.update(documents =>
        picked.reduce((list, document) => this.addDocument(list, document), documents)
      );
      this.emitValue(this.formValue());
    }
  }

  public removeDocument(index: number): void {
    this.documents.update(documents => documents.filter((_document, position) => position !== index));
    this.emitValue(this.formValue());
  }

  public rejected(rejection: JuiImageRejection): void {
    const key = REJECTION_KEYS[rejection.reason];
    this.rejection.set(key ? this.translate(key) : null);
  }

  public offersCamera(): boolean {
    return this.photoCapture.isAvailable();
  }

  public captureWithCamera(openPicker: () => void): Promise<string | null> {
    return this.photoCapture.capture(openPicker, this.cameraI18n());
  }

  private addDocument(documents: UploadedDocument[], document: UploadedDocument): UploadedDocument[] {
    const others = documents.filter(existing => existing.name !== document.name);
    return this.vm().sort === 'desc' ? [document, ...others] : [...others, document];
  }

  private documentProblem(file: File, accept: string): string | null {
    if (!isAcceptedFile(file, accept)) {
      return 'fileTypeNotAllowedError';
    }
    return file.size > MAX_DOCUMENT_BYTES ? 'fileTooLarge' : null;
  }

  private syncImages(sources: string[]): void {
    const current = this.images();
    const unchanged =
      sources.length === current.length && sources.every((source, index) => source === current[index].answer);
    if (unchanged) {
      return;
    }
    const next = sources.map(source => current.find(image => image.answer === source) ?? this.storedImage(source));
    this.release(current.filter(image => !next.includes(image)));
    this.images.set(next);
  }

  private storedImage(source: string): ImageItem {
    this.nextStoredId += 1;
    const id = `stored-${this.nextStoredId}`;
    if (!isStoredImageUrl(source)) {
      return { id, answer: source, displaySrc: source, displayState: 'ready' };
    }
    void this.loadStored(id, source);
    return { id, answer: source, displaySrc: '', displayState: 'loading' };
  }

  private async loadStored(id: string, source: string): Promise<void> {
    try {
      const displaySrc = await this.storedImages.displayUrlFor(source);
      this.updateImage(id, { displaySrc, displayState: 'ready' });
    } catch {
      this.updateImage(id, { displayState: 'error' });
    }
  }

  private updateImage(id: string, changes: Partial<ImageItem>): void {
    this.images.update(images => images.map(image => (image.id === id ? { ...image, ...changes } : image)));
  }

  private release(images: ImageItem[]): void {
    images.forEach(image => this.storedImages.release(image.displaySrc));
  }

  private formValue(): ImageOrFileAnswer[] {
    return [...this.documents(), ...this.images().map(image => image.answer)];
  }

  private translate(key: string): string {
    return this.translations.getTranslation(key);
  }

  private cameraI18n(): CameraI18nKeys {
    return Object.fromEntries(CAMERA_KEYS.map(key => [key, this.translate(key)])) as CameraI18nKeys;
  }
}
